import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { FiTrash } from 'react-icons/fi';
import { CardBrand, OperationType, TransactionStatus } from '../../../domain/transaction';
import { TRANSACTIONS_DEFAULT_RANGE_DAYS, TransactionFiltersQueryParams } from '../hooks/useTransactionFilters';
import { PaymentMethod } from '../../../domain/payment-method';
import { MerchantBranchSelector } from '../../../components/MerchantBranchSelector';
import { DateRange, DynamicDateRange } from '../../../components/DateRangeSelector/DynamicDateRange';
import { Selector } from '../../../components/Selector';
import { SearchFilter } from '../../../components/SearchFilter';
import ExportButton from '../../../components/ExportButton';
import { FULL_EXPORT_FORMATS } from '../../../domain/ExportFormat.const';
import { AmountRange, AmountRangeFilter } from './AmountRangeFilter';

export interface TransactionFilterProps {
  onUpdateFilters: (value: string | undefined, key: keyof TransactionFiltersQueryParams) => void;
  queryParams: TransactionFiltersQueryParams;
  updateParams: (queryParams: TransactionFiltersQueryParams) => void;
  handleResetQueryParamsFilters: () => void
}

export const TransactionFilters = ({ onUpdateFilters, queryParams, updateParams, handleResetQueryParamsFilters }: TransactionFilterProps) => {


  const handleUpdate = useCallback((value: string | undefined, key: keyof TransactionFiltersQueryParams) => {
    onUpdateFilters(value, key);
  }, [onUpdateFilters]);

  const handleDateRangeChange = (range: DateRange) => {
    updateParams({
      page: queryParams.page,
      size: queryParams.size,
      start: range.startDate?.toISOString().split('T')[0] || undefined,
      end: range.endDate?.toISOString().split('T')[0] || undefined,
    })
  }

  const handleAmountRangeChange = (range: AmountRange) => {
    updateParams({ ...range, page: 0, size: queryParams.size });
  };

  return (
    <Box width="full">
      <Flex marginBottom="l" alignItems="center">
        <Heading as="h2" fontSize="xl">Historial de transacciones</Heading>
        <ExportButton params={queryParams} fileName='Reporte-transacciones' path={'/transactions-view/report'} availableFormats={FULL_EXPORT_FORMATS} />
        <Button
          color="primary.800"
          variant="ghost"
          onClick={handleResetQueryParamsFilters}>
          <FiTrash fontSize="1.2em" />
          <Text marginLeft="l">Limpiar filtros</Text>
        </Button>
      </Flex>

      <Flex flexWrap="wrap" alignItems="center" paddingX="xl" marginBottom="l">
        <MerchantBranchSelector
          idBranch={queryParams?.idBranch}
          idMerchant={queryParams?.idMerchant}
          handleUpdate={(key, value) => onUpdateFilters(value, key)}
          marginRight="l"
        />
      </Flex>

      <Accordion allowToggle marginBottom="xxl" marginX="xl" padding="l" borderWidth="1px" borderColor="neutral.200" borderRadius="xl" overflow="show">
        <AccordionItem border="none">
          <AccordionButton>
            <Flex width="full" justifyContent="space-between">
              <Box>Filtros</Box>
              <Box><AccordionIcon /></Box>
            </Flex>
          </AccordionButton>
          <AccordionPanel>
            <Flex alignItems="center" wrap="wrap" zIndex={200}>
              <DynamicDateRange
                onDateRangeChange={handleDateRangeChange}
                endDate={queryParams?.end}
                startDate={queryParams?.start}
                defaultRangeDays={TRANSACTIONS_DEFAULT_RANGE_DAYS}
                margin="l"
              />

              <SearchFilter
                value={queryParams?.serialNumber}
                handleChange={(value) => onUpdateFilters(value, 'serialNumber')}
                placeholder="Buscar por terminal"
                digitsOnly
                width="20em"
                debounceDelay={1000}
              />

              <SearchFilter
                value={queryParams?.operationNumber}
                handleChange={(value) => onUpdateFilters(value, 'operationNumber')}
                placeholder="Buscar por número de operación"
                digitsOnly
                width="20em"
                margin="l"
                debounceDelay={1000}
              />

              <Selector
                isMulti={true}
                options={[
                  { value: TransactionStatus.approved, label: 'Aprobada' },
                  { value: TransactionStatus.reversed, label: 'Reversada' },
                  { value: TransactionStatus.rejected, label: 'Rechazada' },
                ]}
                onChange={(option) => handleUpdate(option, 'status')}
                placeholder='Estado'
                value={queryParams?.status}
                margin="l"
                width="20em"
              />

              <Selector
                isMulti={true}
                options={[
                  { value: PaymentMethod.credit, label: 'Crédito' },
                  { value: PaymentMethod.debit, label: 'Débito' },
                  { value: PaymentMethod.qr, label: 'Código QR' },
                  { value: PaymentMethod.prepaid, label: 'Prepago' },
                ]}
                onChange={(option) => handleUpdate(option, 'paymentMethod')}
                placeholder="Medios de pago"
                value={queryParams?.paymentMethod}
                margin="l"
                width="20em"
              />

              <Selector
                isMulti={true}
                options={[
                  { value: CardBrand.visa, label: 'Visa' },
                  { value: CardBrand.mastercard, label: 'Mastercard' },
                  { value: CardBrand.amex, label: 'American Express' },
                  { value: CardBrand.wuzi, label: 'Wuzi' },
                  { value: CardBrand.otras, label: 'Otras' },
                ]}
                onChange={(option) => handleUpdate(option, 'cardBrand')}
                placeholder="Tarjetas"
                value={queryParams?.cardBrand}
                margin="l"
                width="20em"
              />

              <Selector
                isMulti={true}
                options={[
                  { value: OperationType.payment, label: 'Pago' },
                  { value: OperationType.annulment, label: 'Anulación' },
                  { value: OperationType.refund, label: 'Devolución' },
                ]}
                onChange={(option) => handleUpdate(option, 'operationType')}
                placeholder="Operación"
                value={queryParams?.operationType}
                margin="l"
                width="20em"
              />

              <AmountRangeFilter
                amountStart={queryParams?.amountStart}
                amountEnd={queryParams?.amountEnd}
                onChange={handleAmountRangeChange}
                margin="l"
              />

            </Flex>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};
